/**
 * Shared types and enums for SignalR enterprise services
 */

/**
 * Possible states of a SignalR connection
 */
export enum ConnectionState {
  Connecting = 'Connecting',
  Connected = 'Connected',
  Reconnecting = 'Reconnecting',
  Disconnecting = 'Disconnecting',
  Disconnected = 'Disconnected',
  Failed = 'Failed',
}

/**
 * Result of a message transformation
 */
export interface TransformationResult {
  success: boolean;
  envelope?: unknown;
  error?: Error;
  timestamp: Date;
}

/**
 * Metadata about a specific log context
 */
export interface LogContext {
  correlationId: string;
  timestamp: Date;
  level: 'debug' | 'info' | 'warn' | 'error';
  [key: string]: unknown;
}

/**
 * Hook execution information for observability
 */
export interface HookExecutionInfo {
  hookName: string;
  priority: number;
  status: 'success' | 'error' | 'skipped';
  duration?: number;
  error?: Error;
  timestamp: Date;
  description?: string;
  version?: string;
  author?: string;
  tags?: string[];
  modifiesData?: boolean;
  impactLevel?: 'low' | 'medium' | 'high';
}

/**
 * Subscriber delivery information
 */
export interface SubscriberDeliveryInfo {
  subscriberId: string;
  topic: string;
  status: 'delivered' | 'error' | 'skipped';
  processingTime?: number;
  error?: Error;
  timestamp: Date;
  description?: string;
  version?: string;
  author?: string;
  tags?: string[];
  modifiesData?: boolean;
  impactLevel?: 'low' | 'medium' | 'high';
  isBlocking?: boolean;
}

/**
 * Message metadata for logging and transformation
 */
export interface MessageMetadata {
  eventName: string;
  messageId: string;
  serverId: string;
  topic: string;
  timestamp: Date;
  transport: string;
  protocol: string;
}

/**
 * Handler execution result
 */
export interface HandlerResult {
  success: boolean;
  processed: boolean;
  error?: Error;
  timestamp: Date;
}

/**
 * Reconnection attempt information
 */
export interface ReconnectionAttempt {
  attemptNumber: number;
  error?: Error;
  nextRetryDelayMs?: number;
  timestamp: Date;
}

/**
 * Health check status
 */
export interface HealthStatus {
  name: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  responseTime?: number;
  timestamp: Date;
  details?: Record<string, unknown>;
  lastCheck?: Date;
}

/**
 * Validation result for handler registry
 */
export interface HandlerValidationResult {
  valid: boolean;
  missingHandlers: string[];
  errors: string[];
  timestamp: Date;
}

/**
 * Configuration for backoff strategy
 */
export interface BackoffConfig {
  initialDelayMs: number;
  maxDelayMs: number;
  multiplier: number;
  maxAttempts: number;
}

/**
 * Configuration for SignalR connection
 */
export interface SignalRConnectionConfig {
  url: string;
  timeout: number;
  withCredentials: boolean;
  skipNegotiation: boolean;
  transports: string[];
}

/**
 * All 12 SignalR event definitions
 */
export const SIGNALR_EVENTS = [
  // Error handling
  {
    name: 'ReceiveError',
    type: 'error' as const,
    target: 'Caller' as const,
    mustHandle: true,
    description: 'Server error response',
  },
  // Server monitoring
  {
    name: 'ReceiveServerMonitoringJoined',
    type: 'status' as const,
    target: 'Caller' as const,
    mustHandle: true,
    description: 'Confirmation: Server monitoring started',
  },
  {
    name: 'ReceiveServerMonitoringLeft',
    type: 'status' as const,
    target: 'Caller' as const,
    mustHandle: true,
    description: 'Confirmation: Server monitoring stopped',
  },
  // Group operations - broadcasts
  {
    name: 'ReceiveGroupJoined',
    type: 'group-broadcast' as const,
    target: 'Group' as const,
    mustHandle: true,
    description: 'Notification: User joined group (broadcast to all)',
  },
  {
    name: 'ReceiveGroupLeft',
    type: 'group-broadcast' as const,
    target: 'Group' as const,
    mustHandle: true,
    description: 'Notification: User left group (broadcast to all)',
  },
  // Group operations - confirmations to actor
  {
    name: 'ReceiveGroupJoinedConfirm',
    type: 'confirmation' as const,
    target: 'Caller' as const,
    mustHandle: true,
    critical: true,
    description: 'Confirmation: Successfully joined group',
  },
  {
    name: 'ReceiveGroupLeftConfirm',
    type: 'confirmation' as const,
    target: 'Caller' as const,
    mustHandle: true,
    critical: true,
    description: 'Confirmation: Successfully left group',
  },
  // Chat messages
  {
    name: 'ReceiveChatMessage',
    type: 'message' as const,
    target: 'GroupExcept' as const,
    mustHandle: true,
    description: 'Chat message (broadcast except sender)',
  },
  {
    name: 'ReceiveChatMessageConfirm',
    type: 'confirmation' as const,
    target: 'Caller' as const,
    mustHandle: true,
    critical: true,
    description: 'Confirmation: Message sent',
  },
  // Server status
  {
    name: 'ReceiveServerEvent',
    type: 'event' as const,
    target: 'Group' as const,
    mustHandle: true,
    description: 'Server event broadcast',
  },
  {
    name: 'ReceiveServerStatusChanged',
    type: 'status' as const,
    target: 'Group' as const,
    mustHandle: true,
    description: 'Server online/offline status',
  },
  {
    name: 'ReceiveServerDisconnected',
    type: 'status' as const,
    target: 'Group' as const,
    mustHandle: true,
    description: 'Server disconnected',
  },
] as const;

export type SignalREventName = typeof SIGNALR_EVENTS[number]['name'];
export type SignalREventType = typeof SIGNALR_EVENTS[number]['type'];
export type SignalREventTarget = typeof SIGNALR_EVENTS[number]['target'];

/**
 * Type guard for checking if a name is a valid SignalR event
 */
export function isValidSignalREvent(name: string): name is SignalREventName {
  return SIGNALR_EVENTS.some((e) => e.name === name);
}

/**
 * Get event configuration by name
 */
export function getEventConfig(name: string) {
  return SIGNALR_EVENTS.find((e) => e.name === name);
}

/**
 * Get all critical events that require confirmation
 */
export function getCriticalEvents() {
  return SIGNALR_EVENTS.filter((e) => (e as any).critical === true);
}

/**
 * Get all required events
 */
export function getRequiredEvents() {
  return SIGNALR_EVENTS.filter((e) => e.mustHandle === true);
}
